import React, { useState } from 'react';

import Button from '../../components/ui/Button';
import Footer from '../homepage/components/Footer';
import Header from '../../components/ui/Header';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const Contact = () => {
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: '',
		subject: '',
		message: '',
		serviceType: '',
		urgency: 'normal',
	});

	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsSubmitting(true);

		// Simulate form submission
		setTimeout(() => {
			setIsSubmitting(false);
			alert('Thank you for your message! We will contact you within 24 hours.');
			setFormData({
				name: '',
				email: '',
				phone: '',
				subject: '',
				message: '',
				serviceType: '',
				urgency: 'normal',
			});
		}, 2000);
	};

	const contactMethods = [
		{
			icon: 'Phone',
			title: 'Call Us',
			description: 'Speak with our legal team',
			value: '(+234) 909-552-9557',
			action: 'tel:+2349095529557',
			color: '#800020',
			highlight: true,
		},
		{
			icon: 'Mail',
			title: 'Email Us',
			description: 'Send us your inquiry',
			value: 'info@fepalawpartners.com',
			action: 'mailto:info@fepalawpartners.com',
			color: '#DB954E',
		},
		{
			icon: 'MapPin',
			title: 'Visit Us',
			description: 'Our main office location',
			value: 'No 108 Ada George Road\nChinda Junction Mile 4\nPort Harcourt, Rivers State\nNigeria',
			action: null,
			color: '#800020',
		},
		{
			icon: 'Clock',
			title: 'Business Hours',
			description: "When we're available",
			value: 'Mon-Fri: 8:00 AM - 6:00 PM\nSat: 9:00 AM - 2:00 PM\nSun: Emergency Only',
			action: null,
			color: '#DB954E',
		},
	];

	const serviceTypes = [
		'Aviation Law',
		'Banking and Finance',
		'Commercial and Debt Recovery',
		'Corporate Governance',
		'Criminal Defense',
		'Environmental Law',
		'Energy and Natural Resources',
		'Family Law',
		'Foreign Investment',
		'Intellectual Property',
		'Labour/Industrial and Employment',
		'Litigation and Arbitration',
		'Medical/Healthcare',
		'Probate Practice',
		'Sports Law',
		'Tax Law',
		'Real Estate/Property',
		'Other',
	];

	return (
		<>
			<Helmet>
				<title>Contact Us - Fepa Law Partners | Expert Legal Services in Nigeria</title>
				<meta
					name='description'
					content='Contact Fepa Law Partners for expert legal services in Nigeria. Call (+234) 909-552-9557 or email info@fepalawpartners.com. Located in Port Harcourt, Rivers State.'
				/>
				<meta
					name='keywords'
					content='contact lawyer Nigeria, legal consultation Port Harcourt, Fepa Law Partners contact, legal services Nigeria'
				/>
			</Helmet>

			<div className='min-h-screen bg-gray-50'>
				<Header />

				{/* Hero Section */}
				<section className='relative bg-gradient-to-r from-[#800020] to-[#800020]/80 pt-20 pb-16'>
					<div className='absolute inset-0 bg-black/20'></div>
					<div className='relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
						<div className='text-center'>
							<div className='inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6'>
								<Icon name='MessageSquare' size={20} color='white' />
								<span className='text-white font-source font-medium'>
									Get Expert Legal Guidance
								</span>
							</div>
							<h1 className='font-playfair font-bold text-4xl lg:text-6xl text-white mb-6'>
								Contact Our
								<span className='block text-[#DB954E]'>Legal Experts</span>
							</h1>
							<p className='text-xl text-white/90 max-w-3xl mx-auto leading-relaxed'>
								Connect with Fepa Law Partners' experienced attorneys for expert legal guidance and
								support.
							</p>
						</div>
					</div>
				</section>

				{/* Contact Form Section */}
				<section className='py-16 bg-white'>
					<div className='max-w-4xl mx-auto px-4 lg:px-8'>
						<div className='text-center mb-12'>
							<h2 className='text-3xl md:text-4xl font-playfair font-bold text-[#800020] mb-4'>
								Send Us a Message
							</h2>
							<p className='text-lg text-gray-700 font-source max-w-2xl mx-auto'>
								Fill out the form below and we'll get back to you within 24 hours. All consultations
								are confidential and protected by attorney-client privilege.
							</p>
						</div>

						<form onSubmit={handleSubmit} className='bg-gray-50 rounded-2xl p-8 shadow-lg'>
							<div className='grid grid-cols-1 md:grid-cols-2 gap-6 mb-6'>
								{/* Name */}
								<div>
									<label
										htmlFor='name'
										className='block text-sm font-source font-semibold text-gray-700 mb-2'
									>
										Full Name *
									</label>
									<input
										type='text'
										id='name'
										name='name'
										value={formData.name}
										onChange={handleInputChange}
										required
										className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800 placeholder-gray-500'
										placeholder='Enter your full name'
									/>
								</div>

								{/* Email */}
								<div>
									<label
										htmlFor='email'
										className='block text-sm font-source font-semibold text-gray-700 mb-2'
									>
										Email Address *
									</label>
									<input
										type='email'
										id='email'
										name='email'
										value={formData.email}
										onChange={handleInputChange}
										required
										className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800 placeholder-gray-500'
										placeholder='Enter your email address'
									/>
								</div>

								{/* Phone */}
								<div>
									<label
										htmlFor='phone'
										className='block text-sm font-source font-semibold text-gray-700 mb-2'
									>
										Phone Number
									</label>
									<input
										type='tel'
										id='phone'
										name='phone'
										value={formData.phone}
										onChange={handleInputChange}
										className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800 placeholder-gray-500'
										placeholder='Enter your phone number'
									/>
								</div>

								{/* Service Type */}
								<div>
									<label
										htmlFor='serviceType'
										className='block text-sm font-source font-semibold text-gray-700 mb-2'
									>
										Legal Service Needed
									</label>
									<select
										id='serviceType'
										name='serviceType'
										value={formData.serviceType}
										onChange={handleInputChange}
										className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800'
									>
										<option value=''>Select a service type</option>
										{serviceTypes?.map((service) => (
											<option key={service} value={service}>
												{service}
											</option>
										))}
									</select>
								</div>
							</div>

							{/* Subject */}
							<div className='mb-6'>
								<label
									htmlFor='subject'
									className='block text-sm font-source font-semibold text-gray-700 mb-2'
								>
									Subject *
								</label>
								<input
									type='text'
									id='subject'
									name='subject'
									value={formData.subject}
									onChange={handleInputChange}
									required
									className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800 placeholder-gray-500'
									placeholder='Brief description of your legal matter'
								/>
							</div>

							{/* Urgency */}
							<div className='mb-6'>
								<label className='block text-sm font-source font-semibold text-gray-700 mb-3'>
									Urgency Level
								</label>
								<div className='flex flex-wrap gap-4'>
									{[
										{ value: 'low', label: 'Low', color: 'green' },
										{ value: 'normal', label: 'Normal', color: 'blue' },
										{ value: 'high', label: 'High', color: 'orange' },
										{ value: 'urgent', label: 'Urgent', color: 'red' },
									]?.map((urgency) => (
										<label
											key={urgency.value}
											className='flex items-center space-x-2 cursor-pointer'
										>
											<input
												type='radio'
												name='urgency'
												value={urgency.value}
												checked={formData.urgency === urgency.value}
												onChange={handleInputChange}
												className='text-[#800020] focus:ring-[#800020]'
											/>
											<span className='font-source text-gray-700'>{urgency.label}</span>
										</label>
									))}
								</div>
							</div>

							{/* Message */}
							<div className='mb-8'>
								<label
									htmlFor='message'
									className='block text-sm font-source font-semibold text-gray-700 mb-2'
								>
									Message *
								</label>
								<textarea
									id='message'
									name='message'
									value={formData.message}
									onChange={handleInputChange}
									required
									rows={6}
									className='w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-transparent font-source text-gray-800 placeholder-gray-500 resize-vertical'
									placeholder='Please provide details about your legal matter. Include relevant dates, parties involved, and any specific questions you have.'
								/>
							</div>

							{/* Submit Button */}
							<div className='text-center'>
								<Button
									type='submit'
									variant='default'
									size='lg'
									disabled={isSubmitting}
									className='bg-[#800020] hover:bg-[#800020]/90 text-white font-source font-semibold px-12 py-4 shadow-lg disabled:opacity-50'
									iconName={isSubmitting ? 'Loader' : 'Send'}
									iconPosition='left'
									iconSize={18}
								>
									{isSubmitting ? 'Sending Message...' : 'Send Message'}
								</Button>
							</div>

							{/* Privacy Notice */}
							<div className='mt-6 text-center'>
								<p className='text-sm text-gray-600 font-source'>
									All information is
									confidential and protected by attorney-client privilege.
								</p>
							</div>
						</form>
					</div>
				</section>

				{/* Map Section */}
				<section className='py-16 bg-white'>
					<div className='max-w-7xl mx-auto px-4 lg:px-8'>
						<div className='text-center mb-12'>
							<h2 className='text-3xl md:text-4xl font-playfair font-bold text-[#800020] mb-4'>
								Find Us
							</h2>
							<p className='text-lg text-gray-700 font-source max-w-2xl mx-auto'>
								Visit our main office in Port Harcourt, Rivers State. We're conveniently located in
								the heart of the city.
							</p>
						</div>

						<div className='grid grid-cols-1 lg:grid-cols-2 gap-12 items-center'>
							{/* Map */}
							<div className='bg-gray-100 rounded-2xl overflow-hidden shadow-lg'>
								<div className='aspect-video bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center relative'>
									{/* Embedded Google Map */}
									<iframe
										src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3975.123456789!2d7.0123456!3d4.8156789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNMKwNDgnNTYuNCJOIDdwwrAwMCc0NC40IkU!5e0!3m2!1sen!2sng!4v1234567890123!5m2!1sen!2sng'
										width='100%'
										height='100%'
										style={{ border: 0 }}
										allowFullScreen=''
										loading='lazy'
										referrerPolicy='no-referrer-when-downgrade'
										title='FEPA Law Partners Location'
									></iframe>
								</div>
							</div>

							{/* Location Details */}
							<div className='space-y-8'>
								<div className='bg-gray-50 rounded-xl p-6'>
									<div className='flex items-start space-x-4'>
										<div className='w-12 h-12 bg-[#800020]/10 rounded-lg flex items-center justify-center flex-shrink-0'>
											<Icon name='MapPin' size={24} color='#800020' />
										</div>
										<div>
											<h3 className='font-playfair font-bold text-xl text-[#800020] mb-2'>
												Main Office
											</h3>
											<div className='font-source text-gray-700 space-y-1'>
												<p>No 108 Ada George Road</p>
												<p>Chinda Junction Mile 4</p>
												<p>Port Harcourt, Rivers State</p>
												<p className='font-semibold'>Nigeria</p>
											</div>
										</div>
									</div>
								</div>

								<div className='bg-gray-50 rounded-xl p-6'>
									<div className='flex items-start space-x-4'>
										<div className='w-12 h-12 bg-[#DB954E]/10 rounded-lg flex items-center justify-center flex-shrink-0'>
											<Icon name='Clock' size={24} color='#DB954E' />
										</div>
										<div>
											<h3 className='font-playfair font-bold text-xl text-[#800020] mb-2'>
												Office Hours
											</h3>
											<div className='font-source text-gray-700 space-y-1'>
												<p>
													<span className='font-semibold'>Monday - Friday:</span> 8:00 AM - 6:00 PM
												</p>
												<p>
													<span className='font-semibold'>Saturday:</span> 10:00 AM - 2:00 PM
												</p>
											</div>
										</div>
									</div>
								</div>

								{/* Directions Button */}
								<div className='pt-4'>
									<a
										href='https://maps.google.com/?q=No+108+Ada+George+Road,+Chinda+Junction+Mile+4,+Port+Harcourt,+Rivers+State,+Nigeria'
										target='_blank'
										rel='noopener noreferrer'
										className='inline-flex items-center space-x-2 bg-[#800020] hover:bg-[#800020]/90 text-white px-6 py-3 rounded-lg font-source font-semibold transition-colors duration-200'
									>
										<Icon name='Navigation' size={18} />
										<span>Get Directions</span>
									</a>
								</div>
							</div>
						</div>
					</div>
				</section>

				<Footer />
			</div>
		</>
	);
};

export default Contact;
